/**
 * Pure display logic for the COP dock sidebar.
 *
 * Everything here is backend-driven and read-only: the operator can never edit
 * dock status or operational phase. Keeping the mapping pure (no React, no I/O)
 * makes the precedence and capability rules directly testable.
 */

import type { DockCapability, DockCapabilityType, DockPresence, DockStatus } from '@/types/dock';
import type { LinkFreshness } from '@/lib/drone-telemetry';
import {
  AIRBORNE_LABEL,
  BAY_POSITION_LABEL,
  DOCK_CAPABILITY,
  DOCK_PHASE_LABEL,
  DOCK_STATUS_LABEL,
  PRESENCE_LABEL,
} from '@/lib/copy';

/**
 * `tray` wire values, mirroring the backend's constants in
 * `src/services/airscort-adapter.ts`.
 *
 *   0 closed - 1 OPEN (movement complete) - 2 opening - 3 closing
 *
 * SETTLED AT DESK, 2026-08-23: the board reports 1 for open. STATUS.md records
 * 4/4 bay confirmations that session, and every one of them came through
 * `waitForBayOpen`, which returns only on `tray === TRAY_OPEN` (1) - so the
 * hardware answered 1, four times out of four. The "the board reports tray=2
 * for open" note this file used to carry was a relic of the pre-#326 16-byte
 * misparse era, not a second reading of the board.
 *
 * 2 and 3 are TRANSITIONAL and must never display or gate as open: at 3 the bay
 * is actively shutting.
 */
export const TRAY_CLOSED = 0;
export const TRAY_OPEN = 1;
export const TRAY_OPENING = 2;
export const TRAY_CLOSING = 3;

/**
 * Operator-facing name for a `tray` value, or null for a byte we do not know.
 *
 * The wire calls it a tray; the operator is told "bay", because that is the one word this
 * product now uses for the lid and the slot it covers (see `lib/copy/dock.ts`).
 */
export function trayLabelFor(tray: number): string | null {
  if (tray === TRAY_CLOSED) return BAY_POSITION_LABEL.closed;
  if (tray === TRAY_OPEN) return BAY_POSITION_LABEL.open;
  if (tray === TRAY_OPENING) return BAY_POSITION_LABEL.opening;
  if (tray === TRAY_CLOSING) return BAY_POSITION_LABEL.closing;
  return null;
}

/** The four semantic colors the sidebar is allowed to use. */
export type StatusTone = 'green' | 'amber' | 'red' | 'neutral';

/**
 * A usable reading, or null when the dock has not reported one.
 *
 * Live dock state is merged from MQTT publishes, and the staleness sweep emits a
 * PARTIAL status ({dock_id, status, is_online}) — so any individual field can be
 * absent at runtime whatever the type says. Everything the sidebar renders goes
 * through here so a missing reading shows as "—" instead of crashing or, worse,
 * printing a fabricated 0.
 */
export function reading(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Format a reading for display, or "—" when it was never reported. */
export function formatReading(
  value: unknown,
  { digits = 0, unit = '' }: { digits?: number; unit?: string } = {},
): string {
  const n = reading(value);
  return n === null ? '—' : `${n.toFixed(digits)}${unit}`;
}

/**
 * A dock that has not reported within this window is treated as OFFLINE.
 * Mirrors the backend sweep (DOCK_STALE_AFTER_S, default 90s) so the COP does
 * not keep showing a stale dock as live while waiting for the next sweep tick.
 */
export const DOCK_STALE_AFTER_MS = 90_000;

/** The words themselves live in `lib/copy/dock.ts`; this type follows them. */
export type DockStatusLabel = (typeof DOCK_STATUS_LABEL)[DockStatus];

/**
 * Tone by backend enum, not by label.
 *
 * Keying on the enum is what lets the words change without touching a colour: the wire value
 * is the stable thing, and it is what the tone was always really about.
 */
const STATUS_TONES: Record<DockStatus, StatusTone> = {
  awaiting_connection: 'neutral',
  offline: 'red',
  emergency: 'red',
  tray_open: 'amber',
  // Amber like the open bay, never green: a bay in transit is not a state an
  // operator may read as settled.
  tray_moving: 'amber',
  swapping: 'amber',
  charging: 'amber',
  ready: 'green',
};

/** Backend enum → operator-facing label. Precedence itself is resolved server
 *  side (deriveStatusEnum: emergency > tray > swap > charging > ready). */
const STATUS_ENUM_LABELS: Record<DockStatus, DockStatusLabel> = DOCK_STATUS_LABEL;

export interface DockStatusInput {
  /** Latest known status enum (live WS state, else the registry row). */
  status: DockStatus | null;
  isOnline: boolean;
  /** ISO timestamp of the last live status, or null when none has arrived. */
  receivedAt: string | null;
  nowMs: number;
}

export interface DockStatusView {
  label: DockStatusLabel;
  tone: StatusTone;
  /** True when the displayed hardware values are last-known, not live. */
  stale: boolean;
}

/**
 * Overall dock status shown next to the close button.
 *
 * Connection state wins over hardware state: once the dock stops reporting we
 * no longer know whether it is in emergency, swapping or ready, so claiming any
 * of those would be presenting stale data as live.
 */
export function dockStatusView({ status, isOnline, receivedAt, nowMs }: DockStatusInput): DockStatusView {
  if (status === 'awaiting_connection') {
    return { label: DOCK_STATUS_LABEL.awaiting_connection, tone: 'neutral', stale: false };
  }

  const ageMs = receivedAt === null ? null : nowMs - new Date(receivedAt).getTime();
  const stale = ageMs !== null && Number.isFinite(ageMs) && ageMs > DOCK_STALE_AFTER_MS;

  if (!isOnline || stale) {
    return { label: DOCK_STATUS_LABEL.offline, tone: 'red', stale: true };
  }

  const enumValue: DockStatus = status ?? 'awaiting_connection';
  return { label: STATUS_ENUM_LABELS[enumValue], tone: STATUS_TONES[enumValue], stale: false };
}

/**
 * Tone for a dock state.
 *
 * Takes the backend enum rather than the label it was given before: the tone was always about
 * the state, and keying it on the words meant a rename could silently change a colour.
 */
export function statusTone(status: DockStatus): StatusTone {
  return STATUS_TONES[status];
}

// ---------------------------------------------------------------------------
// Operational phase — where the dock is in the takeoff/landing workflow.
// Distinct from dock status, which describes overall dock health.
// ---------------------------------------------------------------------------

/** The words live in `lib/copy/dock.ts`; this type follows them. */
export type DockOperationalPhase = (typeof DOCK_PHASE_LABEL)[keyof typeof DOCK_PHASE_LABEL];

const PHASE_TONES: Record<DockOperationalPhase, StatusTone> = {
  [DOCK_PHASE_LABEL.preparingTakeoff]: 'amber',
  [DOCK_PHASE_LABEL.readyTakeoff]: 'green',
  [DOCK_PHASE_LABEL.takingOff]: 'amber',
  [DOCK_PHASE_LABEL.preparingLanding]: 'amber',
  [DOCK_PHASE_LABEL.readyLanding]: 'green',
  [DOCK_PHASE_LABEL.landing]: 'amber',
};

/**
 * Map an AirScort LifecycleEvent phase onto the operator-facing phase.
 *
 * The dock contract (docs/airscort-api-spec.md §6) defines exactly four phases,
 * and whether the Pi publishes them at all is still an open question with
 * AirScort — so today this returns null for every dock and the sidebar shows
 * "AWAITING DATA" in gray. READY FOR TAKEOFF / READY FOR LANDING have no source
 * whatsoever and are deliberately never inferred: the dock has to assert them.
 */
export function operationalPhaseFromEvent(phase: unknown): DockOperationalPhase | null {
  switch (phase) {
    case 'pre_takeoff':
      return DOCK_PHASE_LABEL.preparingTakeoff;
    case 'post_takeoff':
      return DOCK_PHASE_LABEL.takingOff;
    case 'pre_land':
      return DOCK_PHASE_LABEL.preparingLanding;
    case 'post_land':
      return DOCK_PHASE_LABEL.landing;
    default:
      return null;
  }
}

export function operationalPhaseTone(phase: DockOperationalPhase | null): StatusTone {
  return phase === null ? 'neutral' : PHASE_TONES[phase];
}

// ---------------------------------------------------------------------------
// Capabilities (docks.capabilities, migration 017)
// ---------------------------------------------------------------------------

export function hasCapability(
  capabilities: readonly DockCapability[] | null | undefined,
  type: DockCapabilityType,
): boolean {
  return (capabilities ?? []).some((c) => c.type === type);
}

/**
 * How many bays to render.
 *
 * Capability is the primary source ('dual' = two bays), but a bay that actually
 * holds a drone is always shown: hiding an assigned airframe because the
 * capability list was never filled in would lose operational information.
 */
export function dockBayCount(
  capabilities: readonly DockCapability[] | null | undefined,
  occupiedBayIndices: readonly (number | null)[] = [],
): number {
  const fromCapability = hasCapability(capabilities, 'dual') ? 2 : 1;
  const highestOccupied = occupiedBayIndices.reduce<number>(
    (max, index) => (typeof index === 'number' && index > max ? index : max),
    0,
  );
  return Math.max(fromCapability, highestOccupied);
}

/** e.g. "Two-drone dock, charger, battery swap". */
export function dockTypeLabel(capabilities: readonly DockCapability[] | null | undefined): string {
  const parts: string[] = [
    hasCapability(capabilities, 'dual') ? DOCK_CAPABILITY.dual : DOCK_CAPABILITY.basic,
  ];
  if (hasCapability(capabilities, 'charger')) parts.push(DOCK_CAPABILITY.charger);
  if (hasCapability(capabilities, 'battery_switch')) parts.push(DOCK_CAPABILITY.batterySwap);
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// Drone presence
// ---------------------------------------------------------------------------

const PRESENCE_VIEWS: Record<DockPresence, { label: string; tone: StatusTone }> = {
  in_dock: { label: PRESENCE_LABEL.in_dock, tone: 'green' },
  not_in_dock: { label: PRESENCE_LABEL.not_in_dock, tone: 'neutral' },
  unknown: { label: PRESENCE_LABEL.unknown, tone: 'amber' },
};

/**
 * Whether the bay's drone is flying right now, and how fresh that claim is.
 * `false` = not flying; otherwise the link freshness of the telemetry that
 * says it is (matches LinkFreshness in drone-telemetry.ts). A link-lost
 * drone stays airborne — its last report said flying, and downgrading it to
 * a pad state would re-open launch on a drone that may still be in the air.
 *
 * THAT LAST RULE IS BOUNDED BY ALTITUDE SINCE #338 — see `bayAirborneFrom`. It was written when
 * "flying" meant "above 2 m", where "it may still be up there" is the right guess. The aircraft's
 * own `is_flying` made `flying` reachable ON THE PAD, and there the guess inverts.
 */
export type BayAirborne = false | 'live' | 'stale' | 'lost';

/**
 * Above this last-known altitude, a lost link still means "assume it is up there".
 *
 * The same 2 m the platform uses everywhere for "clear of the pad" (`FLYING_ALTITUDE_M` server
 * side, `AIRBORNE_ABOVE_M` in drone-command-availability). Named rather than inlined because it is
 * doing a different job here: not "is it flying" but "is its last known position far enough off the
 * pad that silence should be read as still flying".
 */
const LOST_LINK_TRUSTED_ABOVE_M = 2;

/**
 * The bay's airborne verdict, bounded by what the aircraft's last position can support.
 *
 * A LIVE or STALE link is the aircraft still talking, so its claim stands as it always did. A LOST
 * link is a guess, and the guess is only good while the last thing we saw was an aircraft off the
 * pad.
 *
 * WHAT THIS STOPS (#338). `useWebSocket` derives `status` from `is_flying || altitude > 2`, and
 * unlike the server it has no freshness gate — nothing but a new telemetry frame ever rewrites a
 * drone in that map, and the staleness sweep publishes an alert, not a frame. So a DJI held at ~1 m
 * by landing protection whose publisher then dies — RC powered down, app killed, MQTT lost at
 * touchdown, which is the ordinary end of a landing on this airframe — froze at `status: 'flying'`
 * for the rest of the session. The bay row read "IN AIR · LINK LOST" in red and all three LAUNCH
 * buttons stayed disabled, for an aircraft the operator could see sitting in the bay, with nothing
 * short of a page reload to clear it. Meanwhile the server, whose own age gate had long since
 * expired, considered that launch perfectly legitimate.
 *
 * Deliberately NOT a general fix for the client's missing freshness gate, which is a bigger piece
 * of work: it bounds the one consequence that takes a control away from an operator.
 */
export function bayAirborneFrom(
  freshness: LinkFreshness,
  lastKnownAltitudeM: number,
): BayAirborne {
  if (freshness !== 'lost') return freshness;
  // An UNKNOWN last position keeps the old conservative answer. The rest of the client already
  // treats `altitude` as possibly absent at runtime (`droneMapGeoJson` and the selection chip both
  // coalesce it), and here absence is the PERMISSIVE direction: `undefined > 2` is false, which
  // would re-open LAUNCH off a frame that never said where the aircraft was. The same rigour this
  // change applies to an absent `is_flying` — absence is never a "no" — has to apply to this too.
  if (!Number.isFinite(lastKnownAltitudeM)) return 'lost';
  return lastKnownAltitudeM > LOST_LINK_TRUSTED_ABOVE_M ? 'lost' : false;
}

/**
 * Airborne wins over pad presence: a flying drone is EXPECTED to be absent
 * from the pad, so "In the air" is the operational truth while "Not in the dock"
 * or "Unknown" would read as an anomaly. Green matches the COP's flying color
 * (state card, strip board, map markers); a stale or lost link is never
 * presented as a live claim.
 */
export function presenceView(
  presence: DockPresence,
  airborne: BayAirborne = false,
): { label: string; tone: StatusTone } {
  if (airborne === 'lost') return { label: AIRBORNE_LABEL.linkLost, tone: 'red' };
  if (airborne === 'stale') return { label: AIRBORNE_LABEL.linkWeak, tone: 'amber' };
  if (airborne) return { label: AIRBORNE_LABEL.flying, tone: 'green' };
  return PRESENCE_VIEWS[presence];
}

/**
 * PRESENCE DOES NOT VETO A LAUNCH - it warns. It used to, and on 2026-08-23,
 * the night M-001's `drone_on_hub` byte first reached the COP, that veto locked
 * the launch button while the operator was looking at a Mavic sitting on the
 * tray. One unproven sensor byte had acquired the power to ground the fleet.
 * It happened a second time on 2026-08-26, at the site, because the fix sat
 * unmerged - hence this comment rather than a one-line change.
 *
 * The byte is worth showing and not worth obeying alone: it has never been
 * validated against ground truth, and everything that actually makes a launch
 * safe is enforced elsewhere and server-side - the GO/NO-GO gate (dock ready +
 * weather + battery + geofence) and the bay-open confirmation that refuses to
 * command flight through a bay the dock has not confirmed (#113/#117/#120).
 * NOT IN DOCK stays loud in the row, so an operator who cannot see the aircraft
 * is still told; the operator who CAN see it is no longer overruled by a sensor.
 *
 * `airborne` still vetoes, and that one is not a guess: it comes from the
 * aircraft's own telemetry, and launching an aircraft that is already flying is
 * incoherent on any reading.
 */
export function canLaunchFromBay(presence: DockPresence, airborne: BayAirborne = false): boolean {
  void presence;
  return !airborne;
}

// ---------------------------------------------------------------------------
// Tone → Tailwind. One place, so "amber means active process" stays true
// everywhere in the sidebar.
// ---------------------------------------------------------------------------

/** Badge/pill styling (border + tinted background + text). */
export const TONE_BADGE: Record<StatusTone, string> = {
  green: 'border-status-green/40 bg-status-green/[0.08] text-status-green-fg',
  amber: 'border-status-amber/40 bg-status-amber/[0.09] text-status-amber-fg',
  red: 'border-status-red/45 bg-status-red/[0.09] text-status-red-fg',
  neutral: 'border-status-gray/35 bg-status-gray/[0.08] text-status-gray-fg',
};

/** Text-only styling for inline status values. */
export const TONE_TEXT: Record<StatusTone, string> = {
  green: 'text-status-green-fg',
  amber: 'text-status-amber-fg',
  red: 'text-status-red-fg',
  neutral: 'text-status-gray-fg',
};
